import React, { useEffect, useRef, useState } from 'react';
import { tenantApi } from '../services/tenantApi';
import { SignalRService } from '../services/signalr';
import { useRealtimeRefresh } from '../shared/useRealtimeRefresh';
import { PortalBanner } from './PortalBanner';
import type { PortalBannerItem } from '../shared/portalBannerStore';

type Props = {
  audience: 'tenant-admin' | 'tenant-user' | 'platform';
};

export const NotificationDeliveryIncidentBanner: React.FC<Props> = ({ audience }) => {
  const [items, setItems] = useState<PortalBannerItem[]>([]);
  const refreshRef = useRef<() => void>(() => undefined);
  const realtime = audience !== 'platform';
  useRealtimeRefresh(() => refreshRef.current(), realtime);

  useEffect(() => {
    let mounted = true;
    let refreshInFlight = false;
    let rerunRequested = false;

    const refresh = async (): Promise<void> => {
      if (refreshInFlight) {
        rerunRequested = true;
        return;
      }
      refreshInFlight = true;
      try {
        if (audience === 'platform') {
          const summary = await tenantApi.getPlatformNotificationIncidentSummary();
          if (!mounted) return;
          setItems(summary.activeCriticalCount > 0 ? [{
            id: 'platform-critical-delivery-incident',
            title: 'Critical email delivery impairment',
            message: `${summary.activeCriticalCount} active critical delivery incident${summary.activeCriticalCount === 1 ? '' : 's'} require platform review.`,
            variant: 'danger',
            dismissible: false,
          }] : []);
        } else {
          const incidents = await tenantApi.getNotificationDeliveryIncidents({ take: 100, criticalOnly: true });
          if (!mounted) return;
          const active = incidents.filter(incident => incident.state === 'Active' && incident.severity === 'Critical');
          setItems(active.length ? [{
            id: 'tenant-critical-delivery-incidents',
            title: 'Critical email delivery impairment',
            message: `${active.length} active critical delivery incident${active.length === 1 ? '' : 's'} require review in Notifications.`,
            variant: 'danger',
            dismissible: false,
            audience,
          }] : []);
        }
      } catch {
        if (mounted) {
          setItems([{
            id: 'delivery-health-unavailable',
            title: 'Delivery health unavailable',
            message: 'Current delivery incident status could not be verified. Treat the displayed health as stale.',
            variant: 'warning',
            dismissible: false,
            audience: audience === 'platform' ? 'all' : audience,
          }]);
        }
      } finally {
        refreshInFlight = false;
        if (rerunRequested && mounted) {
          rerunRequested = false;
          void refresh();
        }
      }
    };

    refreshRef.current = () => void refresh();
    void refresh();
    // Tenant portals get pushed incident changes; the super-admin portal has no SignalR connection so it polls.
    const interval = realtime ? null : window.setInterval(() => void refresh(), 60_000);
    const unsubscribe = realtime
      ? SignalRService.getInstance().onNotificationDeliveryIncidentsChanged(() => void refresh())
      : () => undefined;
    window.addEventListener('focus', refresh);
    return () => {
      mounted = false;
      if (interval !== null) window.clearInterval(interval);
      unsubscribe();
      window.removeEventListener('focus', refresh);
    };
  }, [audience, realtime]);

  if (!items.length) return null;
  return <PortalBanner items={items} audience={audience === 'platform' ? 'all' : audience} onDismiss={() => undefined} />;
};
